const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/store/workItemsForDocumentsToSign.ts';
import Imm from 'immutable';
import { IActionHandlersMap, makeReducer, ReducerState } from '../defs/actionTypes';
import { createSelector } from 'reselect';
import { sortObjectsByPath } from '../utils/data';
import {
    addRequestActionHandlers,
    defaultKisApiActionSuccess,
    getData,
    isLoading,
    removeStructure,
    useRemoteData2,
    useSaveDataRemotely,
} from '../utils/redux4Requests';
import { CommonStoreState } from '../reducers';
import { useDispatch } from 'react-redux';
import {
    actionSaveSignedPDF,
    atLoadWorkItemsToSign,
    atSaveSignedPDF,
    loadWorkItemsToSign,
    LoadWorkItemsToSignAction,
    SaveSignedPDFAction,
} from '../actions/workItemActions';
import { actionLoadPDFRecord, atLoadPDFRecord, LoadPDFRecordAction } from '@cistec/common_web/actions/imagesActions';

const KEY_PDF_SIGNING = 'PDFSigning';
const KEY_PDF = 'PDF';
const KEY_WORKITEMS_TO_SIGN = 'records';

const handlers: IActionHandlersMap = {};

const createWorkItemsToSignData = (action: LoadWorkItemsToSignAction) => Imm.fromJS(action.payload?.List || {});

addRequestActionHandlers(handlers, atLoadWorkItemsToSign, [KEY_WORKITEMS_TO_SIGN], createWorkItemsToSignData);

const getKeys = (action: LoadPDFRecordAction) => {
    return [KEY_PDF, action.meta.sender, action.meta.icode];
};

const createPDFData = (action: LoadPDFRecordAction) => {
    const list = Imm.fromJS(action.payload?.List || {});
    const entry = list.first();
    let data: unknown = '';

    if (entry && Imm.Iterable.isIterable(entry)) {
        data = entry.get('CONTENT');
    }

    return data;
};

addRequestActionHandlers(handlers, atLoadPDFRecord, getKeys, createPDFData);

const getSavingKeys = (action: SaveSignedPDFAction) => [KEY_PDF_SIGNING, action.meta.workid];

const handleSaveSignedPDFSuccess = (state: ReducerState, action: SaveSignedPDFAction) => {
    state = removeStructure(state, [KEY_WORKITEMS_TO_SIGN]);
    state = defaultKisApiActionSuccess(state, action.payload, getSavingKeys(action));
    return state;
};

addRequestActionHandlers(handlers, atSaveSignedPDF, getSavingKeys, undefined, handleSaveSignedPDFSuccess);

export const reducer = makeReducer(handlers);

export interface SigningItem {
    id: string;
    link?: Imm.Map<string, any>;
    linkedRec?: Imm.Map<string, any>;
    primaryText: string;
    secondaryText: string;
    sortValue: any;
}

export type SigningItemList = Imm.List<SigningItem>;

const selectReducerState = (state: CommonStoreState): ReducerState => state.workItemsToSign;

const selectSigningItems = createSelector([selectReducerState], (reducerState: ReducerState): SigningItemList => {
    const result: SigningItem[] = [];
    const workItemsToSign = getData(reducerState, [KEY_WORKITEMS_TO_SIGN]);
    workItemsToSign?.forEach((item: Imm.Map<string, any>) => {
        if (item) {
            const pdf = item.getIn(['CONTENT', 'PDF']);
            result.push({
                id: item.get('WORKID'),
                link: pdf
                    ? Imm.fromJS({
                          ICODE: pdf.get('ICODE'),
                          SENDER: pdf.get('SENDER'),
                      })
                    : undefined,
                linkedRec: item,
                primaryText: item.get('PATNAME'),
                secondaryText: item.getIn(['CONTENT', 'Report', 'NAME']),
                sortValue: item.get('STARTDAT'),
            });
        }
    });
    return Imm.List<SigningItem>(result.sort(sortObjectsByPath('sortValue')));
});

const makeSelectPDF = (sender: string, code: string) =>
    createSelector([selectReducerState], (reducerState: ReducerState): string | undefined =>
        getData(reducerState, [KEY_PDF, sender, code])
    );

export const makeSelectIsStoringSignedPDF = (workId: string) =>
    createSelector([selectReducerState], (reducerState: ReducerState): boolean | undefined =>
        isLoading(reducerState, [KEY_PDF_SIGNING, workId])
    );

export function useSigningData() {
    return useRemoteData2('workItemsToSign', {
        request: dispatch => dispatch(loadWorkItemsToSign('POOL', 'MobUntschr')),
        keys: [KEY_WORKITEMS_TO_SIGN],
        select: selectSigningItems,
    });
}

export const useSaveSignedPDF = (workid: string) => {
    const dispatch = useDispatch();

    const save = (pdfString: string) => {
        dispatch(actionSaveSignedPDF(workid, pdfString));
    };

    return useSaveDataRemotely('workItemsToSign', save, [KEY_PDF_SIGNING, workid]);
};

export function usePDFData(sender: string, code: string) {
    return useRemoteData2('workItemsToSign', {
        canRequest: !!sender && !!code,
        request: dispatch => dispatch(actionLoadPDFRecord(sender, code)),
        keys: [KEY_PDF, sender, code],
        select: makeSelectPDF(sender, code),
    });
}
